import { REPORT_IDS } from './reports.js';
/**
 * Scheduled report definitions — the "automatic report builder" (Michael, 2026-09-17,
 * #4): "I want an automatic report builder under the admin tab. I want control over
 * the frequency, the emails that receive it, which employees are included, which time
 * entry fields are included, and whether bonus/PTO/commission data is included."
 *
 * 🔥 WHY THIS IS DATA AND NOT ANOTHER CRON JOB. Every scheduled report WorkLogix has
 * today (attendance, payroll gaps, long-shift, approval sweep) is its own Container
 * Apps Job with its own `main.ts` and its own cron expression configured in Azure.
 * That model cannot express what was asked for: you cannot provision an Azure job per
 * report a user invents in the admin tab. So a schedule is a ROW, one generic runner
 * reads the rows, and adding a report becomes an insert rather than a deploy.
 *
 * THIS MODULE IS PURE. Deciding whether a schedule is due is the part that is easy to
 * get subtly wrong — and impossible to test if it is welded to a clock, a database and
 * a mail transport. The runner supplies the local date and the minutes into that day;
 * everything here is a total function of its arguments.
 */
/** How often a schedule fires. */
export const REPORT_SCHEDULE_FREQUENCIES = ['daily', 'weekly', 'monthly'];
/**
 * Which employees a schedule covers.
 *
 * `supervisor` splits the send per supervisor — the shape Michael asked for on the
 * weekly unapproved-hours notice ("it would be great if it was split by supervisor"),
 * so each supervisor receives only their own people rather than the whole company.
 */
export const REPORT_SCHEDULE_SCOPES = ['all', 'supervisor', 'selected'];
/**
 * The time-entry columns a schedule may include.
 *
 * Named explicitly rather than "whatever the timecard renders", so that adding a
 * column to the UI cannot silently change what lands in somebody's inbox every week.
 */
export const REPORT_SCHEDULE_FIELDS = [
    'employee',
    'department',
    'supervisor',
    'day',
    'clockIn',
    'clockOut',
    'hours',
    'overtimeHours',
    'approval',
    'approvedBy',
    'postResult',
    'flags',
    /**
     * What the clocked hours are worth (Michael, 2026-09-24: "We should be able to
     * select whether or not to calculate the pay the employees should receive from their
     * clocked hours").
     *
     * ⚠️ AN ESTIMATE, AND LABELLED AS ONE. It is hours × the employee's rate for that
     * department, and it deliberately does NOT model overtime multipliers, shift
     * differentials or anything NetSuite does at paycheck time. It answers "does this
     * week look about right" before payroll runs — not "what will be paid". Naming the
     * column anything that implied otherwise would invite somebody to reconcile against
     * it.
     */
    'pay',
];
/** A schedule with no fields chosen would render an empty sheet; this is the floor. */
export const DEFAULT_REPORT_SCHEDULE_FIELDS = [
    'employee',
    'department',
    'day',
    'clockIn',
    'clockOut',
    'hours',
    'approval',
];
/**
 * The column each field renders as.
 *
 * 🔥 ONE SPEC, THREE CONSUMERS — the checkbox in the form, the on-screen preview and
 * the emailed workbook. It lives in shared precisely because the preview's whole claim
 * is "this is what will arrive": a header or an ordering kept separately in the web app
 * and the workbook builder would drift, and the first symptom would be a preview that
 * quietly lies.
 *
 * `header` is what the spreadsheet prints; `label` is what the admin ticks. They differ
 * where the form needs to explain itself ("Est. pay (hours × rate)") and the sheet needs
 * to stay narrow ("Est. pay").
 */
export const REPORT_COLUMN_SPEC = {
    employee: { header: 'Employee', label: 'Employee', width: 26, numeric: false },
    department: { header: 'Department', label: 'Department', width: 22, numeric: false },
    supervisor: { header: 'Supervisor', label: 'Supervisor', width: 24, numeric: false },
    day: { header: 'Day', label: 'Day', width: 12, numeric: false },
    clockIn: { header: 'Clock in', label: 'Clock in', width: 12, numeric: false },
    clockOut: { header: 'Clock out', label: 'Clock out', width: 12, numeric: false },
    hours: { header: 'Hours', label: 'Hours', width: 10, numeric: true },
    overtimeHours: { header: 'Overtime', label: 'Overtime', width: 10, numeric: true },
    approval: { header: 'Approval', label: 'Approval status', width: 14, numeric: false },
    approvedBy: { header: 'Approved by', label: 'Approved by', width: 24, numeric: false },
    postResult: { header: 'Post result', label: 'Post result', width: 16, numeric: false },
    flags: { header: 'Flags', label: 'Flags', width: 34, numeric: false },
    // "Estimated" is in the HEADER, not a footnote. A column headed "Pay" in a
    // spreadsheet gets reconciled against; one headed "Est. pay" does not.
    pay: { header: 'Est. pay', label: 'Est. pay (hours × rate)', width: 12, numeric: true },
};
/**
 * Turn the chosen fields into ordered columns.
 *
 * 🔥 THE ORDER IS THE CANONICAL ONE, NOT THE ORDER THE ADMIN TICKED THE BOXES. A report
 * that arrives every week with its columns in a different arrangement is one somebody
 * has to re-read each time, and any downstream sheet that references a column position
 * silently breaks. Ticking a box changes WHICH columns appear, never where they sit.
 *
 * Unknown values are dropped rather than throwing: a field removed from the codebase
 * would otherwise make every schedule still naming it fail to send, which is a much
 * worse outcome than one missing column.
 */
export function reportColumnsFor(fields) {
    const wanted = new Set(fields);
    return REPORT_SCHEDULE_FIELDS.filter((f) => wanted.has(f)).map((key) => ({
        key,
        header: REPORT_COLUMN_SPEC[key].header,
        width: REPORT_COLUMN_SPEC[key].width,
        numeric: REPORT_COLUMN_SPEC[key].numeric,
    }));
}
/** Day-of-month values above this do not occur in every month. */
const SHORTEST_MONTH = 28;
/**
 * Is this schedule due to send?
 *
 * Takes the LOCAL date and how far into that local day it is, both supplied by the
 * caller, so this function never touches a clock or a time zone and can be tested
 * exhaustively.
 *
 * 🔥 THE RUNNER FIRES HOURLY; A SCHEDULE MUST SEND ONCE. The gate is `lastRunOn`, not
 * the hour: once today's send is recorded, the schedule is not due again today no
 * matter how often the runner wakes up.
 *
 * AND IT CATCHES UP. The test is `minutesIntoDay >= sendAtMinutes`, not equality, so a
 * runner that misses 07:00 — an outage, a slow deploy, a job that did not start — still
 * sends at 08:00. A late report is worth having; a skipped one is the failure this
 * shape exists to avoid. The cost is bounded by `lastRunOn`: at most one late send,
 * never a burst.
 */
export function isScheduleDue(schedule, localDate, minutesIntoDay) {
    if (!schedule.active) {
        return false;
    }
    // Already sent today. Checked FIRST: it is the cheapest test and the one that makes
    // an hourly runner safe.
    if (schedule.lastRunOn === localDate) {
        return false;
    }
    if (minutesIntoDay < schedule.sendAtMinutes) {
        return false;
    }
    const day = dayOfWeekFor(localDate);
    if (day === null) {
        // An unparseable date must not fire a send at an unknown moment.
        return false;
    }
    switch (schedule.frequency) {
        case 'daily':
            return true;
        case 'weekly':
            return schedule.dayOfWeek === day;
        case 'monthly':
            return matchesDayOfMonth(schedule.dayOfMonth, localDate);
    }
}
/**
 * Day of month, with the short-month case handled rather than ignored.
 *
 * A schedule set to the 31st would otherwise never fire in February, April, June,
 * September or November — silently, for up to a year. It fires on the LAST day of a
 * month too short to contain it, which is what "monthly on the 31st" plainly means.
 */
function matchesDayOfMonth(dayOfMonth, localDate) {
    if (dayOfMonth === null) {
        return false;
    }
    const dom = dayNumber(localDate);
    if (dom === null) {
        return false;
    }
    if (dom === dayOfMonth) {
        return true;
    }
    if (dayOfMonth <= SHORTEST_MONTH) {
        return false;
    }
    const last = lastDayOfMonth(localDate);
    return dom === last && dayOfMonth > last;
}
/** `YYYY-MM-DD` → 0=Sunday..6=Saturday, or null when it does not parse. */
export function dayOfWeekFor(localDate) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(localDate)) {
        return null;
    }
    // Parsed as UTC deliberately: the caller has ALREADY resolved the local date, so this
    // is calendar arithmetic on a plain date and must not re-apply an offset.
    const d = new Date(`${localDate}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? null : d.getUTCDay();
}
function dayNumber(localDate) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(localDate)) {
        return null;
    }
    return Number.parseInt(localDate.slice(8, 10), 10);
}
function lastDayOfMonth(localDate) {
    const year = Number.parseInt(localDate.slice(0, 4), 10);
    const month = Number.parseInt(localDate.slice(5, 7), 10);
    // Day 0 of the NEXT month is the last day of this one.
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/**
 * Validate a schedule before it is saved.
 *
 * Refusing a nonsensical schedule AT SAVE TIME is the whole point: the alternative is a
 * row that quietly never fires, or fires at a time nobody chose, and is discovered when
 * somebody notices a report they expected has not arrived for a month.
 *
 * Recipients are checked for SHAPE only. Whether an address may actually be written to
 * is the allow-list's decision, which is ops-controlled and deliberately not editable
 * from the admin tab — see the runner.
 */
export function validateSchedule(schedule) {
    const problems = [];
    if (schedule.name.trim() === '') {
        problems.push({ field: 'name', message: 'Give the report a name.' });
    }
    if (schedule.recipients.length === 0) {
        problems.push({ field: 'recipients', message: 'Add at least one recipient.' });
    }
    for (const r of schedule.recipients) {
        if (!r.includes('@') || r.trim() !== r) {
            problems.push({ field: 'recipients', message: `"${r}" is not an email address.` });
        }
    }
    if (!Number.isInteger(schedule.sendAtMinutes) ||
        schedule.sendAtMinutes < 0 ||
        schedule.sendAtMinutes > 24 * 60 - 1) {
        problems.push({ field: 'sendAtMinutes', message: 'Pick a time of day to send.' });
    }
    if (schedule.frequency === 'weekly' &&
        (schedule.dayOfWeek === null || schedule.dayOfWeek < 0 || schedule.dayOfWeek > 6)) {
        problems.push({ field: 'dayOfWeek', message: 'Pick a day of the week.' });
    }
    if (schedule.frequency === 'monthly' &&
        (schedule.dayOfMonth === null || schedule.dayOfMonth < 1 || schedule.dayOfMonth > 31)) {
        problems.push({ field: 'dayOfMonth', message: 'Pick a day of the month (1–31).' });
    }
    problems.push(...validateSelection(schedule));
    return problems;
}
/**
 * Validate the CONTENT of a report — the half a one-off run shares with a schedule.
 *
 * Split out of {@link validateSchedule} rather than copied, so "run this once" and
 * "send this every Wednesday" cannot disagree about what a legal report looks like.
 * A one-off that the builder accepted and the server refused — or worse, accepted and
 * rendered empty — is the same invisible failure the schedule validator exists to stop.
 */
export function validateSelection(selection) {
    const problems = [];
    if (selection.fields.length === 0) {
        problems.push({ field: 'fields', message: 'Include at least one time-entry field.' });
    }
    if (selection.scope === 'selected' &&
        selection.employeeIds.length === 0 &&
        selection.departmentIds.length === 0) {
        problems.push({
            field: 'scope',
            message: 'Choose the employees or departments this report covers.',
        });
    }
    for (const s of selection.sections) {
        if (!REPORT_IDS.includes(s)) {
            problems.push({ field: 'sections', message: `"${s}" is not a report.` });
        }
    }
    return problems;
}
//# sourceMappingURL=report-schedule.js.map